/* ==========================================================================
   ONERA — Search overlay + predictive search (Phase 5 · complete)

   Opens editorial search panel from header triggers. Form works without JS.
   Predictive results via Shopify /search/suggest.json when JS is enabled.
   ========================================================================== */
(function () {
  if (window.__oneraSearchInit) return;
  window.__oneraSearchInit = true;

  var MIN_QUERY = 2;
  var DEBOUNCE_MS = 250;
  var SUGGEST_LIMIT = 6;
  var RECENT_KEY = 'oneraRecentSearches';
  var MAX_RECENT = 5;

  var overlay = null;
  var panel = null;
  var inner = null;
  var input = null;
  var searchForm = null;
  var discovery = null;
  var recentModule = null;
  var recentBody = null;
  var predictiveModule = null;
  var predictiveBody = null;
  var emptyModule = null;
  var closeEls = [];
  var lastFocused = null;
  var isOpen = false;
  var keydownBound = false;
  var inputBound = false;
  var keyboardBound = false;
  var scrollEl = null;
  var i18n = {};
  var debounceTimer = null;
  var abortController = null;
  var requestToken = 0;
  var activeQuery = '';

  var lockScroll = function () {
    document.body.classList.add('onera-search-open');
    if (typeof window.oneraScrollLock === 'function') {
      window.oneraScrollLock();
    } else {
      document.documentElement.classList.add('onera-no-scroll');
    }
  };

  var unlockScroll = function () {
    document.body.classList.remove('onera-search-open');
    if (typeof window.oneraScrollUnlock === 'function') {
      window.oneraScrollUnlock();
    } else {
      document.documentElement.classList.remove('onera-no-scroll');
    }
  };

  var isBackdropTarget = function (target) {
    if (!target) return false;
    if (inner && inner.contains(target)) return false;
    if (target.closest && target.closest('[data-onera-search-close]')) return false;
    return true;
  };

  var resetKeyboardOffset = function () {
    if (overlay) {
      overlay.style.removeProperty('--onera-search-keyboard-offset');
    }
  };

  var updateKeyboardOffset = function () {
    if (!overlay || !isOpen || !window.visualViewport) return;
    var viewport = window.visualViewport;
    var offset = Math.max(
      0,
      window.innerHeight - viewport.height - viewport.offsetTop
    );
    overlay.style.setProperty('--onera-search-keyboard-offset', offset + 'px');
  };

  var bindKeyboardSafe = function () {
    if (keyboardBound || !overlay) return;
    keyboardBound = true;
    scrollEl = overlay.querySelector('[data-onera-search-scroll]');

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateKeyboardOffset);
      window.visualViewport.addEventListener('scroll', updateKeyboardOffset);
    }

    if (!input) return;

    input.addEventListener('focus', function () {
      updateKeyboardOffset();
      if (!window.matchMedia('(max-width: 767px)').matches) return;
      window.setTimeout(function () {
        if (!isOpen || !scrollEl) return;
        var field = input.closest('.onera-search__field');
        if (field && typeof field.scrollIntoView === 'function') {
          field.scrollIntoView({ block: 'start', behavior: 'auto' });
        }
      }, 320);
    });
  };

  var parseI18n = function () {
    if (!overlay) return {};
    var el = overlay.querySelector('[data-onera-search-i18n]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return {};
    }
  };

  var setModuleVisible = function (el, visible) {
    if (!el) return;
    if (visible) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  };

  var setState = function (state) {
    if (overlay) {
      overlay.setAttribute('data-onera-search-state', state);
    }
  };

  var clearPredictiveBody = function () {
    if (predictiveBody) {
      predictiveBody.textContent = '';
    }
  };

  var abortFetch = function () {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
  };

  var resetPredictive = function () {
    abortFetch();
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    requestToken += 1;
    activeQuery = '';
    clearPredictiveBody();
    if (input) {
      input.value = '';
    }
    updateIdleUI();
  };

  var loadRecentSearches = function () {
    try {
      var raw = localStorage.getItem(RECENT_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter(function (item) {
          return typeof item === 'string' && item.trim().length >= MIN_QUERY;
        })
        .slice(0, MAX_RECENT);
    } catch (error) {
      return [];
    }
  };

  var saveRecentSearch = function (term) {
    var trimmed = String(term || '').trim();
    if (trimmed.length < MIN_QUERY) return;

    var lower = trimmed.toLowerCase();
    var items = loadRecentSearches().filter(function (item) {
      return item.toLowerCase() !== lower;
    });
    items.unshift(trimmed);
    if (items.length > MAX_RECENT) {
      items = items.slice(0, MAX_RECENT);
    }

    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(items));
    } catch (error) {}

    renderRecentSearches();
  };

  var clearRecentSearches = function () {
    try {
      localStorage.removeItem(RECENT_KEY);
    } catch (error) {}
    renderRecentSearches();
  };

  var renderRecentSearches = function () {
    if (!recentModule || !recentBody) return;

    var query = input ? input.value.trim() : '';
    if (!isOpen || query.length >= MIN_QUERY) {
      setModuleVisible(recentModule, false);
      recentBody.textContent = '';
      return;
    }

    var items = loadRecentSearches();
    if (!items.length) {
      setModuleVisible(recentModule, false);
      recentBody.textContent = '';
      return;
    }

    recentBody.textContent = '';

    var head = createTextEl('div', 'onera-search__recent-head');
    head.appendChild(
      createTextEl(
        'h3',
        'onera-search__recent-label onera-eyebrow',
        i18n.recentHeading || 'Recent'
      )
    );

    var clearBtn = createTextEl(
      'button',
      'onera-search__recent-clear',
      i18n.clearRecent || 'Clear'
    );
    clearBtn.type = 'button';
    clearBtn.setAttribute(
      'aria-label',
      i18n.clearRecentAria || i18n.clearRecent || 'Clear recent searches'
    );
    clearBtn.addEventListener('click', clearRecentSearches);
    head.appendChild(clearBtn);

    var list = createTextEl('ul', 'onera-search__recent-list');
    list.setAttribute('role', 'list');

    items.forEach(function (term) {
      var li = createTextEl('li', 'onera-search__recent-item');
      li.setAttribute('role', 'listitem');
      var link = createTextEl('a', 'onera-search__recent-link', term);
      link.href = buildSearchResultsUrl(term);
      li.appendChild(link);
      list.appendChild(li);
    });

    recentBody.appendChild(head);
    recentBody.appendChild(list);
    setModuleVisible(recentModule, true);
  };

  var updateIdleUI = function () {
    setModuleVisible(discovery, true);
    renderRecentSearches();
    setModuleVisible(predictiveModule, false);
    setModuleVisible(emptyModule, false);
    setState('idle');
  };

  var buildSearchResultsUrl = function (query) {
    var base = i18n.searchUrl || '/search';
    var params = new URLSearchParams();
    params.set('q', query);
    params.set('type', 'product');
    return base + '?' + params.toString();
  };

  var stripDescription = function (value) {
    if (!value) return '';
    var text = String(value)
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (text.length > 120) {
      return text.slice(0, 117) + '…';
    }
    return text;
  };

  var getProductMeta = function (product) {
    var parts = [];
    if (product.type) parts.push(product.type);
    if (product.vendor) parts.push(product.vendor);
    if (parts.length) return parts.join(' · ');
    return i18n.productLabel || 'Product';
  };

  var getPlaceholderInitials = function (title) {
    if (!title) return i18n.placeholderBrand || 'ONERA';
    var words = String(title).trim().split(/\s+/);
    if (words.length >= 2 && words[0][0] && words[1][0]) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (title.length >= 2) return title.slice(0, 2).toUpperCase();
    return i18n.placeholderBrand || 'ONERA';
  };

  var createAffordance = function () {
    return createTextEl('span', 'onera-search__result-affordance', '→');
  };

  var createProductMedia = function (product) {
    var imageUrl =
      product.image || (product.featured_image && product.featured_image.url);
    var title = product.title || '';

    if (imageUrl) {
      var media = createTextEl('span', 'onera-search__result-media');
      var img = document.createElement('img');
      img.className = 'onera-search__result-image';
      img.src = imageUrl;
      img.alt = title;
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 56;
      img.height = 70;
      media.appendChild(img);
      return media;
    }

    var placeholder = createTextEl(
      'span',
      'onera-search__result-media onera-search__result-media--placeholder'
    );
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.appendChild(
      createTextEl(
        'span',
        'onera-search__result-placeholder-label',
        getPlaceholderInitials(title)
      )
    );
    return placeholder;
  };

  var createProductItem = function (product) {
    var title = product.title || '';
    var li = createTextEl('li', 'onera-search__result onera-search__result--product');
    var link = createTextEl('a', 'onera-search__result-link onera-search__result-link--product');
    link.href = product.url || '#';

    link.appendChild(createProductMedia(product));

    var copy = createTextEl('span', 'onera-search__result-copy');
    copy.appendChild(createTextEl('span', 'onera-search__result-title', title));
    copy.appendChild(
      createTextEl('span', 'onera-search__result-meta', getProductMeta(product))
    );
    link.appendChild(copy);

    var affordance = createAffordance();
    affordance.setAttribute('aria-hidden', 'true');
    link.appendChild(affordance);

    li.appendChild(link);
    return li;
  };

  var createArchiveItem = function (item, typeLabel, description) {
    var li = createTextEl('li', 'onera-search__result onera-search__result--archive');
    var link = createTextEl('a', 'onera-search__result-link onera-search__result-link--archive');
    link.href = item.url || '#';

    var copy = createTextEl('span', 'onera-search__result-copy');
    if (typeLabel) {
      copy.appendChild(createTextEl('span', 'onera-search__result-type', typeLabel));
    }
    copy.appendChild(createTextEl('span', 'onera-search__result-title', item.title || ''));
    if (description) {
      copy.appendChild(createTextEl('span', 'onera-search__result-desc', description));
    }
    link.appendChild(copy);

    var affordance = createAffordance();
    affordance.setAttribute('aria-hidden', 'true');
    link.appendChild(affordance);

    li.appendChild(link);
    return li;
  };

  var createTextEl = function (tag, className, text) {
    var el = document.createElement(tag);
    if (className) el.className = className;
    if (text) el.textContent = text;
    return el;
  };

  var buildSuggestUrl = function (query) {
    var params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product,collection,page');
    params.set('resources[limit]', String(SUGGEST_LIMIT));
    params.set('resources[options][unavailable_products]', 'hide');
    params.set(
      'resources[options][fields]',
      'title,product_type,variants.title,vendor'
    );
    return '/search/suggest.json?' + params.toString();
  };

  var createGroup = function (label, items, renderItem) {
    if (!items || !items.length) return null;

    var group = createTextEl('div', 'onera-search__group');
    group.appendChild(createTextEl('h3', 'onera-search__group-label', label));

    var list = createTextEl('ul', 'onera-search__results');
    items.forEach(function (item) {
      list.appendChild(renderItem(item));
    });

    group.appendChild(list);
    return group;
  };

  var renderResults = function (query, data) {
    if (!predictiveBody) return;

    clearPredictiveBody();

    var results = data && data.resources && data.resources.results;
    if (!results) {
      setModuleVisible(recentModule, false);
      setModuleVisible(predictiveModule, false);
      setModuleVisible(emptyModule, true);
      setState('empty');
      return;
    }

    var products = results.products || [];
    var collections = results.collections || [];
    var pages = results.pages || [];
    var totalCount = products.length + collections.length + pages.length;

    if (totalCount === 0) {
      setModuleVisible(recentModule, false);
      setModuleVisible(predictiveModule, false);
      setModuleVisible(emptyModule, true);
      setState('empty');
      return;
    }

    var fragment = document.createDocumentFragment();
    var productGroup = createGroup(i18n.products || 'Products', products, createProductItem);
    var collectionGroup = createGroup(
      i18n.collections || 'Collections',
      collections,
      function (item) {
        return createArchiveItem(
          item,
          i18n.collections || 'Collection',
          stripDescription(item.body)
        );
      }
    );
    var pageGroup = createGroup(i18n.pages || 'Pages', pages, function (item) {
      return createArchiveItem(
        item,
        i18n.pages || 'Page',
        stripDescription(item.body)
      );
    });

    if (productGroup) fragment.appendChild(productGroup);
    if (collectionGroup) fragment.appendChild(collectionGroup);
    if (pageGroup) fragment.appendChild(pageGroup);

    var viewAllWrap = createTextEl('div', 'onera-search__view-all-wrap');
    var viewAll = createTextEl('a', 'onera-search__view-all');
    viewAll.href = buildSearchResultsUrl(query);
    var viewAllTemplate = i18n.viewAllResults || 'View all results for "{query}"';
    viewAll.textContent = viewAllTemplate.replace('{query}', query);
    viewAllWrap.appendChild(viewAll);
    fragment.appendChild(viewAllWrap);

    predictiveBody.appendChild(fragment);
    setModuleVisible(recentModule, false);
    setModuleVisible(predictiveModule, true);
    setModuleVisible(emptyModule, false);
    setState('results');
  };

  var renderLoading = function () {
    if (!predictiveBody) return;
    clearPredictiveBody();
    predictiveBody.appendChild(
      createTextEl('p', 'onera-search__status', i18n.searching || 'Searching…')
    );
    setModuleVisible(recentModule, false);
    setModuleVisible(predictiveModule, true);
    setModuleVisible(emptyModule, false);
    setState('loading');
  };

  var renderError = function () {
    if (!predictiveBody) return;
    clearPredictiveBody();
    predictiveBody.appendChild(
      createTextEl(
        'p',
        'onera-search__status onera-search__status--error',
        i18n.errorFallback || 'Search is temporarily unavailable.'
      )
    );
    setModuleVisible(recentModule, false);
    setModuleVisible(predictiveModule, true);
    setModuleVisible(emptyModule, false);
    setModuleVisible(discovery, false);
    setState('error');
  };

  var handleQuery = function (query) {
    if (!predictiveBody || !predictiveModule) return;

    var trimmed = query.trim();
    activeQuery = trimmed;

    if (trimmed.length < MIN_QUERY) {
      abortFetch();
      clearPredictiveBody();
      updateIdleUI();
      return;
    }

    setModuleVisible(discovery, false);
    setModuleVisible(recentModule, false);
    renderLoading();

    abortFetch();
    abortController = new AbortController();
    var token = ++requestToken;

    fetch(buildSuggestUrl(trimmed), {
      signal: abortController.signal,
      headers: { Accept: 'application/json' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Predictive search failed');
        return response.json();
      })
      .then(function (data) {
        if (token !== requestToken || trimmed !== activeQuery) return;
        renderResults(trimmed, data);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        if (token !== requestToken || trimmed !== activeQuery) return;
        renderError();
      });
  };

  var onInput = function () {
    if (!input) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(function () {
      handleQuery(input.value);
    }, DEBOUNCE_MS);
  };

  var close = function (instant) {
    if (!overlay || !isOpen) return;

    isOpen = false;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    unlockScroll();
    resetKeyboardOffset();
    resetPredictive();

    if (!instant && lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  };

  var open = function (trigger) {
    if (!overlay || isOpen) return;

    if (typeof window.oneraCloseCartDrawer === 'function') {
      window.oneraCloseCartDrawer(true);
    }

    if (typeof window.oneraCloseNavDrawer === 'function') {
      window.oneraCloseNavDrawer(true);
    }

    lastFocused = trigger || document.activeElement;
    isOpen = true;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    lockScroll();

    window.setTimeout(function () {
      if (input && typeof input.focus === 'function') {
        input.focus({ preventScroll: true });
      }
      renderRecentSearches();
    }, 60);
  };

  var onKeydown = function (event) {
    if (event.key === 'Escape' && isOpen) {
      event.preventDefault();
      close();
    }
  };

  var bindTriggers = function () {
    var triggers = document.querySelectorAll('[data-onera-search-open]');
    triggers.forEach(function (trigger) {
      if (trigger.__oneraSearchBound) return;
      trigger.__oneraSearchBound = true;
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        open(trigger);
      });
    });
  };

  var bindPredictive = function () {
    if (!input || !predictiveBody || inputBound) return;
    inputBound = true;
    input.addEventListener('input', onInput);

    if (searchForm) {
      searchForm.addEventListener('submit', function () {
        if (input) saveRecentSearch(input.value);
      });
    }

    predictiveBody.addEventListener('click', function (event) {
      if (activeQuery.length < MIN_QUERY) return;
      var target = event.target;
      if (!target || !target.closest) return;
      if (target.closest('.onera-search__view-all')) {
        saveRecentSearch(activeQuery);
        return;
      }
      if (target.closest('.onera-search__result-link')) {
        saveRecentSearch(activeQuery);
      }
    });
  };

  var bind = function () {
    overlay = document.querySelector('[data-onera-search-overlay]');
    if (!overlay) return;

    panel = overlay.querySelector('[data-onera-search-panel]');
    inner = overlay.querySelector('[data-onera-search-inner]');
    input = overlay.querySelector('.onera-search__input');
    searchForm = overlay.querySelector('.onera-search__form');
    discovery = overlay.querySelector('[data-onera-search-discovery]');
    recentModule = overlay.querySelector('[data-onera-search-recent]');
    recentBody = overlay.querySelector('[data-onera-search-recent-body]');
    predictiveModule = overlay.querySelector('[data-onera-search-predictive]');
    predictiveBody = overlay.querySelector('[data-onera-search-predictive-body]');
    emptyModule = overlay.querySelector('[data-onera-search-empty-preview]');
    i18n = parseI18n();

    bindTriggers();
    bindPredictive();
    bindKeyboardSafe();

    closeEls = Array.prototype.slice.call(
      overlay.querySelectorAll('[data-onera-search-close]')
    );

    closeEls.forEach(function (el) {
      if (el.__oneraSearchCloseBound) return;
      el.__oneraSearchCloseBound = true;
      el.addEventListener('click', function () {
        close();
      });
    });

    if (panel && !panel.__oneraSearchBackdropBound) {
      panel.__oneraSearchBackdropBound = true;
      panel.addEventListener('click', function (event) {
        if (isBackdropTarget(event.target)) {
          close();
        }
      });
    }

    if (!keydownBound) {
      document.addEventListener('keydown', onKeydown);
      keydownBound = true;
    }

    document.addEventListener('onera:cart:open', function () {
      close(true);
    });

    window.oneraCloseSearch = close;
    window.oneraOpenSearch = open;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
})();
